/**
 * Layout tables (fetchUserSite / Site 3D) → Site Layout 2D editor geometry.
 *
 * Site 3D stores layout Z in feet with production world Z = -layoutZ; the editor canvas
 * uses image Y down ≈ layout Z when Z is positive (editor saves). Auto-layout sites often
 * use negative Z — we detect and flip so the 2D view matches Site 3D.
 */
import {
    catalogBinIdFromLayoutBinId,
    layoutSideLooksBinLocal,
} from "./layoutTableProxy.js";
import { findFaceAtPoint } from "./siteLayoutEnclosure.js";
import {
    circleFromThreePoints,
    closedRectIdForFace,
    createFlatRectangleSegments,
    createFlatRectangleSegmentsAtOriented,
    DEFAULT_MARKER_RADIUS_PX,
    FALLBACK_LAYOUT_BIN_DIAMETER_FT,
    defaultLayoutFeetPerPixel,
    getMarkerRadiusImagePx,
    newSegmentId,
} from "./siteLayoutStice.js";

const round1 = (v) => Math.round(v * 10) / 10;

const BLANK_LAYOUT_MIN_SIZE = { width: 2400, height: 1600 };
const BLANK_LAYOUT_MAX_SIZE = { width: 12000, height: 12000 };
const BLANK_LAYOUT_FIT_MARGIN_PX = 96;

/** Image-space bounds of all bins and walls/arcs (for blank-canvas sizing). */
export const getEditorGeometryImageBounds = (
    markers,
    layoutSegments,
    marginPx = 0
) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const addPt = (x, y, r = 0) => {
        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        minX = Math.min(minX, x - r);
        maxX = Math.max(maxX, x + r);
        minY = Math.min(minY, y - r);
        maxY = Math.max(maxY, y + r);
    };

    (markers || []).forEach((m) => {
        const r = getMarkerRadiusImagePx(m);
        addPt(m.x, m.y, r);
    });
    (layoutSegments || []).forEach((seg) => {
        if (seg.x1 != null && seg.y1 != null) {
            addPt(seg.x1, seg.y1, 8);
            addPt(seg.x2, seg.y2, 8);
        }
        if (seg.bulgeX != null && seg.bulgeY != null) {
            addPt(seg.bulgeX, seg.bulgeY, 8);
        }
        if (seg.curveX != null && seg.curveY != null) {
            addPt(seg.curveX, seg.curveY, 8);
        }
    });

    if (!Number.isFinite(minX)) return null;
    return {
        minX: minX - marginPx,
        maxX: maxX + marginPx,
        minY: minY - marginPx,
        maxY: maxY + marginPx,
    };
};

/** Shift all editor geometry by a delta in image px. */
export const translateEditorGeometry = (markers, layoutSegments, dx, dy) => ({
    markers: (markers || []).map((m) => ({
        ...m,
        x: m.x + dx,
        y: m.y + dy,
    })),
    layoutSegments: (layoutSegments || []).map((seg) => {
        const next = { ...seg };
        if (next.x1 != null && next.y1 != null && next.x2 != null && next.y2 != null) {
            next.x1 += dx;
            next.y1 += dy;
            next.x2 += dx;
            next.y2 += dy;
        }
        if (next.bulgeX != null && next.bulgeY != null) {
            next.bulgeX += dx;
            next.bulgeY += dy;
        }
        if (next.curveX != null && next.curveY != null) {
            next.curveX += dx;
            next.curveY += dy;
        }
        if (next.attach1?.x != null && next.attach1?.y != null) {
            next.attach1 = {
                ...next.attach1,
                x: next.attach1.x + dx,
                y: next.attach1.y + dy,
            };
        }
        if (next.attach2?.x != null && next.attach2?.y != null) {
            next.attach2 = {
                ...next.attach2,
                x: next.attach2.x + dx,
                y: next.attach2.y + dy,
            };
        }
        return next;
    }),
});

/** True when all bins/walls fit inside the canvas with the standard fit margin. */
export const editorGeometryFitsBlankCanvas = (
    markers,
    layoutSegments,
    canvasWidth,
    canvasHeight,
    marginPx = BLANK_LAYOUT_FIT_MARGIN_PX
) => {
    const bounds = getEditorGeometryImageBounds(markers, layoutSegments, 0);
    if (!bounds) return true;
    const w = Math.max(canvasWidth, 1);
    const h = Math.max(canvasHeight, 1);
    return (
        bounds.minX >= marginPx &&
        bounds.minY >= marginPx &&
        bounds.maxX <= w - marginPx &&
        bounds.maxY <= h - marginPx
    );
};

/**
 * Grow blank canvas to fit all bins/walls with margin; inset geometry when it overflows.
 * Large sites (e.g. Riceland) exceed the default 2400×1600 grid after Load from Site 3D.
 *
 * preservePositionWhenFits: when restoring a saved editor draft, keep centered geometry
 * instead of shifting everything to the top-left margin.
 */
export const fitBlankLayoutCanvasSize = (
    markers,
    layoutSegments,
    options = {}
) => {
    const minW = Math.max(options.minWidth ?? BLANK_LAYOUT_MIN_SIZE.width, 1);
    const minH = Math.max(options.minHeight ?? BLANK_LAYOUT_MIN_SIZE.height, 1);
    const margin = options.marginPx ?? BLANK_LAYOUT_FIT_MARGIN_PX;
    const maxW = options.maxWidth ?? BLANK_LAYOUT_MAX_SIZE.width;
    const maxH = options.maxHeight ?? BLANK_LAYOUT_MAX_SIZE.height;
    const preservePositionWhenFits = options.preservePositionWhenFits === true;

    const bounds = getEditorGeometryImageBounds(markers, layoutSegments, 0);
    if (!bounds) {
        return {
            width: minW,
            height: minH,
            markers: markers || [],
            layoutSegments: layoutSegments || [],
            siteCenterPx: { x: minW / 2, y: minH / 2 },
            changed: false,
        };
    }

    if (
        preservePositionWhenFits &&
        editorGeometryFitsBlankCanvas(
            markers,
            layoutSegments,
            minW,
            minH,
            margin
        )
    ) {
        return {
            width: minW,
            height: minH,
            markers: markers || [],
            layoutSegments: layoutSegments || [],
            siteCenterPx: { x: minW / 2, y: minH / 2 },
            changed: false,
        };
    }

    const contentW = bounds.maxX - bounds.minX;
    const contentH = bounds.maxY - bounds.minY;
    let width = Math.ceil(Math.max(minW, contentW + 2 * margin));
    let height = Math.ceil(Math.max(minH, contentH + 2 * margin));
    width = Math.min(width, maxW);
    height = Math.min(height, maxH);

    let shiftX = margin - bounds.minX;
    let shiftY = margin - bounds.minY;
    if (preservePositionWhenFits) {
        if (bounds.minX >= margin) shiftX = 0;
        if (bounds.minY >= margin) shiftY = 0;
        width = Math.ceil(Math.max(minW, bounds.maxX + margin));
        height = Math.ceil(Math.max(minH, bounds.maxY + margin));
        width = Math.min(width, maxW);
        height = Math.min(height, maxH);
    }
    const needShift =
        Math.abs(shiftX) > 0.5 || Math.abs(shiftY) > 0.5;
    const changed =
        needShift || width > minW + 0.5 || height > minH + 0.5;

    const translated = needShift
        ? translateEditorGeometry(markers, layoutSegments, shiftX, shiftY)
        : { markers: markers || [], layoutSegments: layoutSegments || [] };

    return {
        width,
        height,
        markers: translated.markers,
        layoutSegments: translated.layoutSegments,
        siteCenterPx: { x: width / 2, y: height / 2 },
        changed,
    };
};

const diameterFtToRadiusPx = (diameterFt, feetPerPixel) => {
    const d = Number(diameterFt);
    if (!(d > 0) || feetPerPixel <= 0) return null;
    return Math.max(6, (d / 2) / feetPerPixel);
};

const median = (arr) => {
    const sorted = arr.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
    if (!sorted.length) return 0;
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
};

/**
 * Layout side endpoint in site layout feet (group origin).
 * Persisted rows use group-local absolute coords (bin offset already in x1/z1).
 */
export const sideEndpointSiteFt = (side, keyX, keyZ, layoutBin, layoutGroup) => {
    const hlX =
        keyX === "x1"
            ? "lx1"
            : keyX === "x2"
              ? "lx2"
              : keyX === "curveX"
                ? "lcurveX"
                : null;
    const hlZ =
        keyZ === "z1"
            ? "lz1"
            : keyZ === "z2"
              ? "lz2"
              : keyZ === "curveZ"
                ? "lcurveZ"
                : null;

    if (
        hlX &&
        hlZ &&
        Number.isFinite(side?.[hlX]) &&
        Number.isFinite(side?.[hlZ])
    ) {
        return {
            x: (layoutGroup?.x ?? 0) + (layoutBin?.x ?? 0) + side[hlX],
            z: (layoutGroup?.z ?? 0) + (layoutBin?.z ?? 0) + side[hlZ],
        };
    }

    let sx = side?.[keyX] ?? 0;
    let sz = side?.[keyZ] ?? 0;

    if (layoutSideLooksBinLocal(side, layoutBin)) {
        return {
            x: (layoutGroup?.x ?? 0) + (layoutBin?.x ?? 0) + sx,
            z: (layoutGroup?.z ?? 0) + (layoutBin?.z ?? 0) + sz,
        };
    }
    return {
        x: (layoutGroup?.x ?? 0) + sx,
        z: (layoutGroup?.z ?? 0) + sz,
    };
};

const isImportableLayoutSide = (side) => !!side;

const BIN_SNAP_TOL_PX = 8;
const PERIM_TOL_RATIO = 0.1;

const binsTouching = (m1, m2) => {
    const r1 = getMarkerRadiusImagePx(m1);
    const r2 = getMarkerRadiusImagePx(m2);
    const d = Math.hypot(m2.x - m1.x, m2.y - m1.y);
    if (d < 1e-3) return false;
    return Math.abs(d - (r1 + r2)) <= Math.max(BIN_SNAP_TOL_PX, (r1 + r2) * 0.06);
};

const endpointOnMarkerPerimeter = (x, y, marker) => {
    const r = getMarkerRadiusImagePx(marker);
    const tol = Math.max(5, r * PERIM_TOL_RATIO);
    return Math.abs(Math.hypot(x - marker.x, y - marker.y) - r) <= tol;
};

/** Straight chord across a snapped bin pair — enclosure uses perimeter arcs instead. */
const isSpuriousInterBinChord = (seg, markers) => {
    if (seg.kind !== "wall" || !seg.markerId1 || !seg.markerId2) return false;
    if (seg.markerId1 === seg.markerId2) return false;
    const m1 = markers.find((mk) => mk.id === seg.markerId1);
    const m2 = markers.find((mk) => mk.id === seg.markerId2);
    if (!m1 || !m2 || !binsTouching(m1, m2)) return false;
    const p1on1 = endpointOnMarkerPerimeter(seg.x1, seg.y1, m1);
    const p2on2 = endpointOnMarkerPerimeter(seg.x2, seg.y2, m2);
    const p1on2 = endpointOnMarkerPerimeter(seg.x1, seg.y1, m2);
    const p2on1 = endpointOnMarkerPerimeter(seg.x2, seg.y2, m1);
    return (p1on1 && p2on2) || (p1on2 && p2on1);
};

/** Layout tables sometimes store bin-pocket curves as straight sides (curveRadius 0). */
const promoteWallAlongSingleMarker = (seg, markers) => {
    if (seg.kind !== "wall") return seg;
    let m = null;
    if (seg.markerId1 && seg.markerId1 === seg.markerId2) {
        m = markers.find((mk) => mk.id === seg.markerId1);
    }
    if (!m) {
        m =
            markers.find(
                (mk) =>
                    endpointOnMarkerPerimeter(seg.x1, seg.y1, mk) &&
                    endpointOnMarkerPerimeter(seg.x2, seg.y2, mk)
            ) ?? null;
    }
    if (!m) return seg;

    let a1 = seg.angle1 ?? Math.atan2(seg.y1 - m.y, seg.x1 - m.x);
    let a2 = seg.angle2 ?? Math.atan2(seg.y2 - m.y, seg.x2 - m.x);
    let delta = a2 - a1;
    while (delta <= 0) delta += 2 * Math.PI;
    while (delta > Math.PI) {
        const t = a1;
        a1 = a2;
        a2 = t;
        delta = 2 * Math.PI - delta;
    }
    const midA = a1 + delta / 2;
    const r = getMarkerRadiusImagePx(m);
    return {
        ...seg,
        kind: "arc",
        isInner: seg.isInner !== false,
        markerId1: m.id,
        markerId2: m.id,
        angle1: a1,
        angle2: a2,
        bulgeX: m.x + r * Math.cos(midA),
        bulgeY: m.y + r * Math.sin(midA),
    };
};

/** After import: drop phantom chords; turn perimeter straight sides into arcs. */
const refineImportedSegments = (markers, segments) => {
    const kept = segments.filter((s) => !isSpuriousInterBinChord(s, markers));
    return kept.map((s) => promoteWallAlongSingleMarker(s, markers));
};

const sideHasArcGeometry = (side) => {
    const cr = side.newCurveRadius ?? side.curveRadius ?? 0;
    return cr > 0.05;
};

/** Arc center in site feet when curveX/Z are missing (chord + radius + isInner). */
const inferArcCenterSiteFt = (p1, p2, radiusFt, isInner) => {
    const mx = (p1.x + p2.x) / 2;
    const mz = (p1.z + p2.z) / 2;
    const chord = Math.hypot(p2.x - p1.x, p2.z - p1.z);
    if (chord < 0.01 || radiusFt <= chord / 2) return { x: mx, z: mz };
    const d = Math.sqrt(radiusFt * radiusFt - (chord / 2) * (chord / 2));
    const nx = -(p2.z - p1.z) / chord;
    const nz = (p2.x - p1.x) / chord;
    const sign = isInner ? -1 : 1;
    return { x: mx + sign * nx * d, z: mz + sign * nz * d };
};

const snapSegmentEndpointsToMarkers = (seg, markers) => {
    const snap = (x, y) => {
        let best = null;
        let bestErr = Infinity;
        (markers || []).forEach((m) => {
            const r = getMarkerRadiusImagePx(m);
            const err = Math.abs(Math.hypot(x - m.x, y - m.y) - r);
            const tol = Math.max(5, r * 0.1);
            if (err <= tol && err < bestErr) {
                bestErr = err;
                best = {
                    markerId: m.id,
                    angle: Math.atan2(y - m.y, x - m.x),
                };
            }
        });
        return best;
    };
    const s1 = snap(seg.x1, seg.y1);
    const s2 = snap(seg.x2, seg.y2);
    if (s1) {
        seg.markerId1 = s1.markerId;
        seg.angle1 = s1.angle;
    }
    if (s2) {
        seg.markerId2 = s2.markerId;
        seg.angle2 = s2.angle;
    }
    return seg;
};

const sideLayoutToSegment = (side, ftToImg, { closedRectId } = {}) => {
    const p1 = sideEndpointSiteFt(side, "x1", "z1", ftToImg.layoutBin, ftToImg.layoutGroup);
    const p2 = sideEndpointSiteFt(side, "x2", "z2", ftToImg.layoutBin, ftToImg.layoutGroup);
    const x1 = ftToImg.x(p1.x);
    const y1 = ftToImg.y(p1.z);
    const x2 = ftToImg.x(p2.x);
    const y2 = ftToImg.y(p2.z);

    if (sideHasArcGeometry(side)) {
        const cr = side.newCurveRadius ?? side.curveRadius ?? 0;
        const hasCurveKey =
            Number.isFinite(side.lcurveX) ||
            Number.isFinite(side.curveX) ||
            Number.isFinite(side.lcurveZ) ||
            Number.isFinite(side.curveZ);

        let pc = hasCurveKey
            ? sideEndpointSiteFt(
                  side,
                  "curveX",
                  "curveZ",
                  ftToImg.layoutBin,
                  ftToImg.layoutGroup
              )
            : inferArcCenterSiteFt(p1, p2, cr, side.isInner);

        let bulgeX = ftToImg.x(pc.x);
        let bulgeY = ftToImg.y(pc.z);
        if (!circleFromThreePoints(x1, y1, x2, y2, bulgeX, bulgeY)) {
            const reinf = inferArcCenterSiteFt(p1, p2, cr, side.isInner);
            bulgeX = ftToImg.x(reinf.x);
            bulgeY = ftToImg.y(reinf.z);
        }

        const seg = {
            id: newSegmentId(),
            kind: "arc",
            isInner: !!side.isInner,
            x1,
            y1,
            x2,
            y2,
            bulgeX,
            bulgeY,
        };
        if (closedRectId) seg.closedRectId = closedRectId;
        return seg;
    }

    const seg = {
        id: newSegmentId(),
        kind: "wall",
        isInner: false,
        x1,
        y1,
        x2,
        y2,
    };
    if (closedRectId) seg.closedRectId = closedRectId;
    return seg;
};

/**
 * @param {object} site — global.gw.selectedSite (layoutGroups, layoutBins, layoutSides, bins)
 * @param {object} options
 * @param {number} options.feetPerPixel
 * @param {number} options.imageWidth
 * @param {number} options.imageHeight
 * @returns {{ markers, layoutSegments, faceAssignments, faceLabels, markerLabels, feetPerPixel, siteCenterPx, layoutBins } | null}
 */
export const layoutTablesToEditorGeometry = (site, options = {}) => {
    const layoutGroups = site?.layoutGroups ?? [];
    const layoutBins = site?.layoutBins ?? [];
    const layoutSides = site?.layoutSides ?? [];
    const siteBins = site?.bins ?? [];

    if (!layoutBins.length) return null;

    const fpp =
        options.feetPerPixel > 0
            ? options.feetPerPixel
            : defaultLayoutFeetPerPixel(siteBins);

    const imgW = Math.max(options.imageWidth || 2400, 1);
    const imgH = Math.max(options.imageHeight || 1600, 1);

    const binsWithGroup = [];
    layoutBins.forEach((lb) => {
        const g = layoutGroups.find(
            (gg) =>
                gg.customerSiteLayoutGroupId === lb.customerSiteLayoutGroupId
        );
        if (!g) return;
        const absX = (g.x ?? 0) + (lb.x ?? 0);
        const absZ = (g.z ?? 0) + (lb.z ?? 0);
        binsWithGroup.push({ lb, g, absX, absZ });
    });

    if (!binsWithGroup.length) return null;

    const zSamples = binsWithGroup.map((b) => b.absZ);
    const flipLayoutZ = median(zSamples) < 0;

    let sumX = 0;
    let sumZ = 0;
    binsWithGroup.forEach((b) => {
        sumX += b.absX;
        sumZ += b.absZ;
    });
    const siteFtOriginX = sumX / binsWithGroup.length;
    const siteFtOriginZ = sumZ / binsWithGroup.length;
    const siteCenterPx = { x: imgW / 2, y: imgH / 2 };

    const ftToImg = {
        x: (ftX) => siteCenterPx.x + (ftX - siteFtOriginX) / fpp,
        y: (ftZ) => {
            const rel = ftZ - siteFtOriginZ;
            return flipLayoutZ
                ? siteCenterPx.y - rel / fpp
                : siteCenterPx.y + rel / fpp;
        },
        layoutBin: null,
        layoutGroup: null,
    };

    const markers = [];
    const layoutSegments = [];
    const faceAssignments = {};
    const faceLabels = {};
    const markerLabels = {};
    const flatAreaSeeds = [];
    const layoutBinById = new Map(
        layoutBins.map((lb) => [lb.customerSiteLayoutBinId, lb])
    );

    binsWithGroup.forEach(({ lb, g, absX, absZ }) => {
        if (!lb.isRound) return;

        const diameter =
            lb.width > 0
                ? lb.width
                : lb.length > 0
                  ? lb.length
                  : FALLBACK_LAYOUT_BIN_DIAMETER_FT;
        const rPx =
            diameterFtToRadiusPx(diameter, fpp) ?? DEFAULT_MARKER_RADIUS_PX;
        const markerId = `m_load_${lb.customerSiteLayoutBinId}`;
        const binDataId = catalogBinIdFromLayoutBinId(lb.binId);
        markers.push({
            id: markerId,
            x: round1(ftToImg.x(absX)),
            y: round1(ftToImg.y(absZ)),
            binDataId,
            radiusPx: rPx,
        });
        if (lb.binName && !binDataId) {
            markerLabels[markerId] = String(lb.binName);
        }
    });

    layoutSides.forEach((side) => {
        const lb = layoutBinById.get(side.customerSiteLayoutBinId);
        if (!isImportableLayoutSide(side)) return;

        if (!lb) return;

        const g = layoutGroups.find(
            (gg) =>
                gg.customerSiteLayoutGroupId === lb.customerSiteLayoutGroupId
        );
        if (!g) return;

        ftToImg.layoutBin = lb;
        ftToImg.layoutGroup = g;

        const closedRectId = lb.isRound
            ? null
            : `load_rect_${lb.customerSiteLayoutBinId}`;

        const seg = sideLayoutToSegment(side, ftToImg, {
            closedRectId: closedRectId || undefined,
        });
        if (seg) layoutSegments.push(seg);
    });

    layoutSegments.forEach((seg) => snapSegmentEndpointsToMarkers(seg, markers));

    const refinedSegments = refineImportedSegments(markers, layoutSegments);
    layoutSegments.length = 0;
    layoutSegments.push(...refinedSegments);

    binsWithGroup.forEach(({ lb, g, absX, absZ }) => {
        if (lb.isRound) return;

        const rectId = `load_rect_${lb.customerSiteLayoutBinId}`;
        flatAreaSeeds.push({
            customerSiteLayoutBinId: lb.customerSiteLayoutBinId,
            binId: catalogBinIdFromLayoutBinId(lb.binId),
            binName: lb.binName ? String(lb.binName) : "",
            cx: round1(ftToImg.x(absX)),
            cy: round1(ftToImg.y(absZ)),
            halfW: Math.max((lb.width || 0) / 2 / fpp, 8),
            halfL: Math.max((lb.length || 0) / 2 / fpp, 8),
        });
        const hasRectSegs = layoutSegments.some((s) => s.closedRectId === rectId);

        if (!hasRectSegs && lb.width > 0 && lb.length > 0) {
            const halfW = lb.width / 2 / fpp;
            const halfL = lb.length / 2 / fpp;
            const cx = ftToImg.x(absX);
            const cy = ftToImg.y(absZ);
            const rotDeg = Number(lb.rotation);
            const segs =
                Number.isFinite(rotDeg) && rotDeg !== 0
                    ? createFlatRectangleSegmentsAtOriented(
                          cx,
                          cy,
                          halfW,
                          halfL,
                          (-rotDeg * Math.PI) / 180,
                          rectId
                      )
                    : createFlatRectangleSegments(
                          cx - halfW,
                          cy - halfL,
                          cx + halfW,
                          cy + halfL,
                          rectId
                      );
            layoutSegments.push(...segs);
        }

        const catalogBinId = catalogBinIdFromLayoutBinId(lb.binId);
        if (catalogBinId != null) {
            faceAssignments[`pending_${rectId}`] = catalogBinId;
        }
        if (lb.binName) {
            faceLabels[`pending_${rectId}`] = String(lb.binName);
        }
    });

    const fitted = fitBlankLayoutCanvasSize(markers, layoutSegments, {
        minWidth: imgW,
        minHeight: imgH,
    });

    return {
        markers: fitted.markers,
        layoutSegments: fitted.layoutSegments,
        faceAssignments,
        faceLabels,
        markerLabels,
        feetPerPixel: fpp,
        siteCenterPx: fitted.siteCenterPx,
        blankImageSize: { width: fitted.width, height: fitted.height },
        layoutBins,
        flipLayoutZ,
        flatAreaSeeds,
    };
};

/** Gap between bin centers on the auto-generated Site 3D square grid (ft). Prevents touching-bin pocket synthesis lag. */
export const AUTO_GRID_BIN_GAP_FT = 10;

const anyMarkersTouching = (markers) => {
    for (let i = 0; i < markers.length; i++) {
        for (let j = i + 1; j < markers.length; j++) {
            if (binsTouching(markers[i], markers[j])) return true;
        }
    }
    return false;
};

/**
 * Spread auto-grid bin markers apart so perimeters do not touch. Site 3D's default square grid
 * places bin centers one diameter apart; enclosure synthesis treats those gaps as assignable areas.
 */
export const separateTouchingAutoGridMarkers = (geom, gapFt = AUTO_GRID_BIN_GAP_FT) => {
    const markers = geom?.markers;
    if (!markers?.length || gapFt <= 0) return geom;
    if (!anyMarkersTouching(markers)) return geom;

    const fpp =
        geom.feetPerPixel > 0
            ? geom.feetPerPixel
            : defaultLayoutFeetPerPixel([]);
    const gapPx = gapFt / fpp;

    let minDist = Infinity;
    for (let i = 0; i < markers.length; i++) {
        for (let j = i + 1; j < markers.length; j++) {
            const d = Math.hypot(
                markers[j].x - markers[i].x,
                markers[j].y - markers[i].y
            );
            if (d > 1e-3 && d < minDist) minDist = d;
        }
    }
    if (!Number.isFinite(minDist) || minDist <= 0) return geom;

    const scale = (minDist + gapPx) / minDist;
    if (scale <= 1.0001) return geom;

    const cx = markers.reduce((s, m) => s + m.x, 0) / markers.length;
    const cy = markers.reduce((s, m) => s + m.y, 0) / markers.length;
    const scaleXY = (x, y) => ({
        x: round1(cx + (x - cx) * scale),
        y: round1(cy + (y - cy) * scale),
    });

    const newMarkers = markers.map((m) => {
        const p = scaleXY(m.x, m.y);
        return { ...m, x: p.x, y: p.y };
    });

    const newSegments = (geom.layoutSegments || []).map((seg) => {
        const p1 = scaleXY(seg.x1, seg.y1);
        const p2 = scaleXY(seg.x2, seg.y2);
        const next = { ...seg, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
        if (seg.bulgeX != null && seg.bulgeY != null) {
            const pb = scaleXY(seg.bulgeX, seg.bulgeY);
            next.bulgeX = pb.x;
            next.bulgeY = pb.y;
        }
        snapSegmentEndpointsToMarkers(next, newMarkers);
        return next;
    });

    const newSeeds = (geom.flatAreaSeeds || []).map((seed) => {
        const p = scaleXY(seed.cx, seed.cy);
        return {
            ...seed,
            cx: p.x,
            cy: p.y,
            halfW: seed.halfW * scale,
            halfL: seed.halfL * scale,
        };
    });

    const fitted = fitBlankLayoutCanvasSize(newMarkers, newSegments, {
        minWidth: geom.blankImageSize?.width ?? BLANK_LAYOUT_MIN_SIZE.width,
        minHeight: geom.blankImageSize?.height ?? BLANK_LAYOUT_MIN_SIZE.height,
    });

    return {
        ...geom,
        markers: fitted.markers,
        layoutSegments: fitted.layoutSegments,
        flatAreaSeeds: newSeeds,
        blankImageSize: { width: fitted.width, height: fitted.height },
        siteCenterPx: fitted.siteCenterPx,
    };
};

/** Map enclosed faces to layout bin assignments after computeEnclosedFaces. */
export const faceAssignmentsFromLayoutTables = (
    faces,
    layoutSegments,
    pendingAssignments,
    pendingLabels = {},
    flatAreaSeeds = []
) => {
    const faceAssignments = {};
    const faceLabels = {};
    const usedFaceIds = new Set();

    faces.forEach((face) => {
        const rectId = closedRectIdForFace(face, layoutSegments);
        if (!rectId || !String(rectId).startsWith("load_rect_")) return;
        const pendingKey = `pending_${rectId}`;
        const binId = pendingAssignments[pendingKey];
        if (binId != null) {
            faceAssignments[face.id] = binId;
            usedFaceIds.add(face.id);
        }
    });

    (flatAreaSeeds || []).forEach((seed) => {
        if (!seed.binId && !seed.binName) return;

        let face = findFaceAtPoint(seed.cx, seed.cy, faces);
        if (!face || usedFaceIds.has(face.id)) {
            face = (faces || []).find((f) => {
                if (usedFaceIds.has(f.id)) return false;
                const dx = Math.abs((f.centroid?.x ?? 0) - seed.cx);
                const dy = Math.abs((f.centroid?.y ?? 0) - seed.cy);
                return dx <= seed.halfW * 2 && dy <= seed.halfL * 2;
            });
        }
        if (!face) return;

        if (seed.binId != null) {
            faceAssignments[face.id] = seed.binId;
        } else if (seed.binName) {
            faceLabels[face.id] = seed.binName;
        }
        usedFaceIds.add(face.id);
    });

    return { faceAssignments, faceLabels };
};

const segmentGeometryKey = (seg) =>
    [
        seg.kind,
        Math.round(seg.x1 ?? 0),
        Math.round(seg.y1 ?? 0),
        Math.round(seg.x2 ?? 0),
        Math.round(seg.y2 ?? 0),
        Math.round(seg.bulgeX ?? 0),
        Math.round(seg.bulgeY ?? 0),
    ].join("|");

/**
 * Restore editor walls/arcs (round-bin perimeters, binArc legs) missing from layoutSides-only import.
 */
export const mergeSavedEditorSegmentsIntoGeometry = (geom, editorPayload) => {
    if (!geom || !editorPayload?.layoutSegments?.length) return geom;

    const loadedMarkers = geom.markers || [];
    const loadedByBinId = new Map();
    loadedMarkers.forEach((m) => {
        if (m.binDataId != null) loadedByBinId.set(String(m.binDataId), m);
    });

    const markerIdMap = new Map();
    (editorPayload.markers || []).forEach((sm) => {
        if (sm.binDataId == null) return;
        const loaded = loadedByBinId.get(String(sm.binDataId));
        if (loaded) markerIdMap.set(sm.id, loaded.id);
    });

    const existingKeys = new Set(
        (geom.layoutSegments || []).map(segmentGeometryKey)
    );
    const added = [];

    editorPayload.layoutSegments.forEach((es) => {
        const remapped = {
            ...es,
            id: newSegmentId(),
            markerId1: es.markerId1
                ? markerIdMap.get(es.markerId1) ?? es.markerId1
                : undefined,
            markerId2: es.markerId2
                ? markerIdMap.get(es.markerId2) ?? es.markerId2
                : undefined,
        };
        const key = segmentGeometryKey(remapped);
        if (existingKeys.has(key)) return;
        snapSegmentEndpointsToMarkers(remapped, loadedMarkers);
        existingKeys.add(key);
        added.push(remapped);
    });

    if (!added.length) return geom;

    return {
        ...geom,
        layoutSegments: [...(geom.layoutSegments || []), ...added],
        faceAssignments: {
            ...(editorPayload.faceAssignments || {}),
            ...(geom.faceAssignments || {}),
        },
        faceLabels: {
            ...(editorPayload.faceLabels || {}),
            ...(geom.faceLabels || {}),
        },
        markerLabels: {
            ...(geom.markerLabels || {}),
            ...(editorPayload.markerLabels || {}),
        },
    };
};
